import React, { useState, useEffect, useCallback, useId } from "react";
import { Input, AutoComplete } from 'antd';
import '@/assets/less/float-input.less';

const FloatInput = ({ 
    label, 
    suffix, 
    type = "text", 
    required, 
    iconRender, 
    onChange, 
    onDebouncedChange,
    prefix, 
    password, 
    disabled, 
    value,
    defaultValue,
    multiline, 
    infoIcon, 
    autoComplete, 
    htmlAutoComplete,
    onClear, 
    width = '100%', 
    suggestions,
    min,
    max,
    digitsOnly = false,
    onKeyDown: onKeyDownProp,
    onPaste: onPasteProp,
    inputMode,
    forceUppercase = false,
    allowCustom = true,
    className,
    style,
    id: idProp,
    ...restProps 
}) => {
    // Form.Item injects id; otherwise generate one so every <label> is programmatically associated.
    const generatedId = useId();
    const inputId = idProp ?? generatedId;

    const [isFocused, setIsFocused] = useState(false);
    const [isCapsLockOn, setIsCapsLockOn] = useState(false);
    const [internalValue, setInternalValue] = useState(
        value !== undefined ? value : (defaultValue !== undefined ? defaultValue : '')
    );
    const debounceTimerRef = React.useRef(null);

    const handleFocus = () => { setIsFocused(true); checkCapsLock(false); }
    const handleBlur = () => {
        if (autoComplete && !allowCustom && currentValue) {
            const allowedOptions = suggestions || restProps.options || [];
            const allowed = allowedOptions.some(option =>
                String(option.value || option.label || '').toLowerCase() === String(currentValue).toLowerCase()
            );
            if (allowedOptions.length > 0 && !allowed) {
                if (value === undefined) {
                    setInternalValue('');
                }
                onChange?.('');
            }
        }
        setIsFocused(false);
        checkCapsLock(false);
    };

    const sanitizeDigits = useCallback((val) => {
        if (!digitsOnly || typeof val !== 'string') {
            return val;
        }
        return val.replace(/\D+/g, '');
    }, [digitsOnly]);

    const handleNumericKeyDown = useCallback((event) => {
        if (!digitsOnly) {
            return;
        }
        if (["e", "E", ".", "+", "-"].includes(event.key)) {
            event.preventDefault();
        }
    }, [digitsOnly]);

    const handleNumericPaste = useCallback((event) => {
        if (!digitsOnly) {
            return;
        }
        const pasted = event.clipboardData?.getData('text') ?? '';
        if (!/^\d*$/.test(pasted)) {
            event.preventDefault();
        }
    }, [digitsOnly]);

    const handleInputChange = (e) => {
        let nextVal = e?.target ? e.target.value : e;

        const sanitizedVal = sanitizeDigits(nextVal);
        if (digitsOnly && typeof nextVal === 'string' && sanitizedVal !== nextVal) {
            nextVal = sanitizedVal;
            if (e?.target) {
                e.target.value = sanitizedVal;
            }
            if (e?.currentTarget) {
                e.currentTarget.value = sanitizedVal;
            }
        }

        if (value === undefined) {
            setInternalValue(nextVal);
        }

        if (onChange) {
            onChange(e?.target ? e : nextVal);
        }
        if (!nextVal && onClear) {
            onClear();
        }

        if (onDebouncedChange) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                onDebouncedChange(sanitizeDigits(nextVal));
            }, 400);
        }
    }

    const handleKeyDown = (e) => {
        checkCapsLock(e.getModifierState('CapsLock'));
    };

    const checkCapsLock = (isActive) => {
        setIsCapsLockOn(isActive);
    };


    useEffect(() => {
        const autofillTest = setInterval(() => {
            const autofill = document.querySelector(`input[name=${restProps.name}]`);
            if (autofill && autofill.matches(':autofill')) {
                setIsFocused(true);
                clearInterval(autofillTest);
            }
        }, 100);
        return () => clearInterval(autofillTest);
    }, [restProps.name]);

    const currentValue = value !== undefined ? value : internalValue;
    const isOccupied = isFocused || (currentValue !== undefined && currentValue !== null && currentValue !== '');
    const inputStyle = {
        ...(style || {}),
        ...(forceUppercase ? { textTransform: 'uppercase' } : {}),
    };

    if (autoComplete) {
        return (
            <div className={`float-label ${isFocused ? "focused" : ""}`}
                style={width ? { width: width } : {}}>
                <AutoComplete
                    defaultValue={defaultValue}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    allowClear={onClear}
                    style={{ width: width, height: "38px" }}
                    onChange={handleInputChange}
                    options={suggestions}
                    value={currentValue}
                    disabled={disabled}
                    variant={"borderless"}
                    filterOption={(inputValue, option) =>
                    option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    {...restProps}
                    id={inputId}
                >
                    <Input
                        className={className}
                        variant={"borderless"}
                        style={{ ...inputStyle, height: "20px", border: "none", boxShadow: "none" }}
                        type={type}
                        prefix={prefix}
                        suffix={suffix}
                        inputMode={digitsOnly ? 'numeric' : inputMode}
                        onKeyDown={(event) => {
                            handleNumericKeyDown(event);
                            if (onKeyDownProp) {
                                onKeyDownProp(event);
                            }
                        }}
                        onPaste={(event) => {
                            handleNumericPaste(event);
                            if (onPasteProp) {
                                onPasteProp(event);
                            }
                        }}
                    />
                </AutoComplete>
                {label && 
                    <label htmlFor={inputId} className={`label ${isOccupied || isFocused ? "label-float" : ""}`}
                        style={{ color: "var(--dms-color-text-muted)", left: prefix ? '31px' : '11px', pointerEvents: 'none' }}
                    >
                        {label}{required && <span className="text-danger">*</span>}
                        {infoIcon && <span style={{ pointerEvents: 'auto', display: 'inline-flex', marginLeft: 4 }}>{infoIcon}</span>}
                    </label>
                }
            </div>
        )
    }

    if (multiline) {
        return (
            <div className={`float-label float-label-multiline ${isFocused ? "focused" : ""}`}
                style={width ? { width: width } : {}}>
                <Input.TextArea
                    className={className}
                    defaultValue={defaultValue}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    value={currentValue} 
                    onChange={handleInputChange} 
                    disabled={disabled} 
                    style={{ ...inputStyle, height: "70px" }}
                    {...restProps} 
                    id={inputId}
                />
                {label && 
                    <label htmlFor={inputId} className={`label ${isOccupied || isFocused ? "label-float" : ""}`}
                        style={{ color: "var(--dms-color-text-muted)", left: prefix ? '31px' : '11px', pointerEvents: 'none' }}
                    >
                        {label}{required && <span className="text-danger">*</span>}
                        {infoIcon && <span style={{ pointerEvents: 'auto', display: 'inline-flex', marginLeft: 4 }}>{infoIcon}</span>}
                    </label>
                }
            </div>
        )
    }

    return (
        <div className={`float-label ${isFocused ? "focused" : ""}`}
            style={width ? { width: width } : {}}>
            {
                !password ?
                    <Input
                        className={className}
                        prefix={prefix}
                        onChange={handleInputChange}
                        type={type}
                        onKeyDown={(event) => {
                            handleNumericKeyDown(event);
                            if (onKeyDownProp) {
                                onKeyDownProp(event);
                            }
                        }}
                        onPaste={(event) => {
                            handleNumericPaste(event);
                            if (onPasteProp) {
                                onPasteProp(event);
                            }
                        }}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        value={currentValue}
                        defaultValue={defaultValue}
                        disabled={disabled}
                        style={{ ...inputStyle, height: "38px" }}
                        suffix={suffix}
                        min={type === "number" ? min : undefined}
                        max={type === "number" ? max : undefined}
                        inputMode={digitsOnly ? 'numeric' : inputMode}
                        autoComplete={htmlAutoComplete}
                        {...restProps}
                        id={inputId}
                    />
                    :
                    <Input.Password
                        className={className}
                        prefix={prefix}
                        iconRender={iconRender}
                        onChange={handleInputChange}
                        type={type}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        value={currentValue}
                        onKeyDown={handleKeyDown}
                        style={{ ...inputStyle, height: "38px" }}
                        suffix={suffix}
                        autoComplete={htmlAutoComplete}
                        {...restProps}
                        id={inputId}
                    />
            }
            <label htmlFor={inputId} className={`label ${isOccupied || isFocused ? "label-float" : ""}`} style={{ color: "var(--dms-color-text-muted)", left: prefix ? '31px' : '11px', pointerEvents: 'none' }}>
                {label} {required && <span className="text-danger">*</span>}
                {infoIcon && <span style={{ pointerEvents: 'auto', display: 'inline-flex', marginLeft: 4 }}>{infoIcon}</span>}
                {isCapsLockOn && <span className="ms-1" style={{ color: 'red' }}>Caps Lock is ON!</span>}
            </label>
        </div>
    );
};

export default FloatInput;
